import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useEffect, useState } from 'react';

interface PlaylistVideoPlayerProps {
  videoUrl: string;
  thumbnail?: string;
  onEnded: () => void;
  autoPlay?: boolean;
  theme: 'purple' | 'blue' | 'dark' | 'white';
}

const themeColors = {
  purple: '#7A5CFF',
  blue: '#2D6CF6',
  dark: '#FFFFFF',
  white: '#7A5CFF',
};

export const PlaylistVideoPlayer: React.FC<PlaylistVideoPlayerProps> = ({
  videoUrl,
  thumbnail,
  onEnded,
  autoPlay = false,
  theme
}) => {
  useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (autoPlay) {
      video.play().catch(err => console.error('Autoplay failed:', err));
    }
  }, [autoPlay, videoUrl]);

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  return (
    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        controls
        poster={thumbnail}
        autoPlay={autoPlay}
        playsInline
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={onEnded}
        style={{
          '--playlist-theme-color': themeColors[theme]
        } as React.CSSProperties}
      >
        <source src={videoUrl} type="video/mp4" />
        {translateInterfaceText("Your browser does not support the video tag.")}</video>

      <style>{`
        video::-webkit-media-controls-timeline {
          background-color: ${themeColors[theme]};
        }
      `}</style>
    </div>
  );
};
